import React, { useRef } from "react";
import ReactDatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

/** Quick ranges next to the calendar (same as the Crew Activity filter). */
export const DATE_RANGE_PRESETS = [
  { label: "Today", getRange: () => [moment(), moment()] },
  { label: "Yesterday", getRange: () => [moment().subtract(1, "day"), moment().subtract(1, "day")] },
  { label: "This Week", getRange: () => [moment().startOf("week"), moment().endOf("week")] },
  {
    label: "Last Week",
    getRange: () => [moment().subtract(1, "week").startOf("week"), moment().subtract(1, "week").endOf("week")],
  },
  { label: "This Month", getRange: () => [moment().startOf("month"), moment().endOf("month")] },
  {
    label: "Last Month",
    getRange: () => [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
  },
];

/**
 * One From - To date box (react-datepicker range) with quick presets, as on the Crew Activity filter.
 * startDate / endDate: Date or null; onChange([start, end]) — end is null while the second date is being picked.
 * The calendar opens above sliders and modals.
 */
export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = "dd/MM/yyyy - dd/MM/yyyy",
  ariaLabel = "Date range",
  presets = DATE_RANGE_PRESETS,
  isClearable = true,
}) {
  const picker = useRef(null);
  const isActive = (preset) => {
    const [start, end] = preset.getRange();
    return Boolean(startDate && endDate && start.isSame(startDate, "day") && end.isSame(endDate, "day"));
  };
  const applyPreset = (preset) => {
    const [start, end] = preset.getRange();
    onChange([start.startOf("day").toDate(), end.startOf("day").toDate()]);
    if (picker.current) picker.current.setOpen(false);
  };
  const renderContainer = ({ className, children }) => (
    <CalendarContainer className={className}>
      <div className="mst-daterange__shell">
        <div className="mst-daterange__presets">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className={`mst-daterange__preset${isActive(preset) ? " is-active" : ""}`}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="mst-daterange__months">{children}</div>
      </div>
    </CalendarContainer>
  );
  const narrow = typeof window !== "undefined" && window.innerWidth < 576;

  return (
    <ReactDatePicker
      ref={picker}
      calendarContainer={renderContainer}
      selectsRange
      startDate={startDate || null}
      endDate={endDate || null}
      onChange={(range) => onChange(range ?? [null, null])}
      dateFormat="dd/MM/yyyy"
      placeholderText={placeholder}
      onChangeRaw={(e) => e.preventDefault()}
      className="mst-daterange__input"
      wrapperClassName="mst-daterange"
      popperClassName="mst-daterange__popper"
      monthsShown={narrow ? 1 : 2}
      showPopperArrow={false}
      isClearable={isClearable}
      portalId="mst-daterange-portal"
      title={ariaLabel}
      name={ariaLabel}
      autoComplete="off"
    />
  );
}
